import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import './index.scss';
import Container from "../Container";
import Context from "../../../context";
import { useLocation, useParams } from "react-router-dom";
import Spinner from "../Spinner";
import Header from "../Header";
import PDFViewerLite from "./PDFViewerLite";
import SingleImageViewer from "./SingleImageViewer";
import { EwaContext } from "../../../context/EwaContext";
import { BACK_NAVIGATION_TYPE } from "../../../helpers/constants";
import { getEwaBackPath } from "../../../libraries/ewa/helpers";
import { TYPE_KEYS } from "../../Directory";

const DocumentViewer = ({ type, dailyData, docData = null, setDocData, hideCloseButton = false }) => {
  const { pathname,  } = useLocation();
  const [loading, setLoading] = useState(true);

  const { viewMode, setScreenNameOverride, itin, customHistory, navMenuItems, isSafari } = useContext(Context);
  const { setEwaDocs, setIsViewerVisible, lastDocIdRef, ewaDocs, navigatedDocsRef, ewaOpenDocumentInfo, setEwaOpenDocumentInfo, setEwaPaths, setScreenNameReady } = useContext(EwaContext);
  const { docId } = useParams();

  const useEwaTitle = useRef(false);

  const isDocuments = type?.includes("Documents");
  const docs = dailyData
    ? dailyData // with the type="dailyDocuments"
    : (type === "singleDocuments" ||type === "mapDocuments")
      ? itin?.documents
      : itin[type]; // for Stays directories | vouchers | dailyActivities
  const item = isDocuments
    ? (docs?.find(item => item.id === parseInt(docId)))
    : docData;
  const mimeType = item?.file?.mimeType;
  const isImage = mimeType?.includes("image");
  const isFullscreenType = useMemo(() => viewMode === "desktop" && ["dailyDocuments", "mapDocuments", ...TYPE_KEYS].includes(type), [viewMode, type]);

  const closePath = useMemo(() => {
    if (customHistory && customHistory.length > 1 && !isFullscreenType) return BACK_NAVIGATION_TYPE;
    const isSingleNavItemDoc = navMenuItems?.find(item => item.path === pathname);
    const ewaBackPath = getEwaBackPath();

    const existingClosePath = (isSingleNavItemDoc && !dailyData)
      ? `/${itin.localData.urlKey}`
      : pathname.replace(/\/view.*/, '');

    return (ewaBackPath && pathname === ewaBackPath?.entryPath) ? ewaBackPath?.returnTo : existingClosePath;
  }, [isFullscreenType, customHistory, navMenuItems, dailyData, itin?.localData?.urlKey, pathname]);

  useEffect(() => {
    if (ewaOpenDocumentInfo?.screenName && pathname === ewaOpenDocumentInfo.ewaSourcePath) {
      useEwaTitle.current = true;
      setEwaOpenDocumentInfo(prev => ({
        ...prev,
        updated: true,
      }));
    }

    if (ewaOpenDocumentInfo?.screenName) {
      if (pathname === ewaOpenDocumentInfo.ewaSourcePath) {
        setScreenNameOverride(ewaOpenDocumentInfo.screenName);
        useEwaTitle.current = true; // mark that we used the EWA title
      } else {
        // Navigated away from EWA path, fallback to item name
        setScreenNameOverride(item?.name);
        useEwaTitle.current = false;
      }
    } else if (!useEwaTitle.current) {
      // No EWA title saved, use the item's name if not already using it
      if (mimeType === "application/zip") setScreenNameReady(false);
      setScreenNameOverride(item?.name);
    }

    if (ewaOpenDocumentInfo?.screenName) return;
    return () => {
      useEwaTitle.current = false;
      setScreenNameOverride(null);
    };
  }, [setScreenNameReady, mimeType, pathname, item?.name, setScreenNameOverride, setEwaOpenDocumentInfo, ewaOpenDocumentInfo?.ewaSourcePath, ewaOpenDocumentInfo?.screenName]);

  useEffect(() => {
    if (mimeType === "application/zip") {
      setIsViewerVisible(true);
      if (lastDocIdRef.current !== item.id) {
        lastDocIdRef.current = item.id;
        setEwaPaths(prev => new Set([...prev, pathname]));
      }
      if (!ewaDocs.some(doc => doc.id === item.id)) {
        setEwaDocs(prev => [...prev, { ...item, fullscreen: isFullscreenType }]);
      }
    } else {
      setIsViewerVisible(false);
    }
  }, [isFullscreenType, setEwaPaths, ewaDocs, item, lastDocIdRef, mimeType, pathname, setEwaDocs, setIsViewerVisible, setScreenNameOverride]);

  useEffect(() => {
    const navigatedDocs = navigatedDocsRef.current;

    return () => {
      setIsViewerVisible(false);
      if (navigatedDocs && !navigatedDocs.has(item.id)) {
        setEwaDocs(prev => prev.filter(doc => doc.id !== item.id));
        lastDocIdRef.current = null;
      }
    };
  }, [item?.id, navigatedDocsRef, setEwaDocs, setIsViewerVisible, lastDocIdRef]);

  useEffect(() => () => {
    setDocData && setDocData(null);
  }, [setDocData]);

  const onClose = () => {
    setDocData && setDocData(null);
  };

  const handleLoad = () => {
    setLoading(false);
  };

  const renderVideo = () => (
    <video
      controls
      width="100%"
      height="100%"
      style={{ display: loading ? 'none' : 'block' }}
      onLoadStart={handleLoad}
    >
      <source src={item?.file?.httpsUrl} type={mimeType || 'video/mp4'} />
      Your browser does not support the video tag.
    </video>
  );

  // for single image
  const renderImage = () => (
    <div className="doc-viewer-img-container">
      <SingleImageViewer
        src={item?.file?.httpsUrl}
        alt={item?.name || ''}
        onLoad={handleLoad}
      />
    </div>
  );

  const renderIframe = () => (
    <iframe
      title="Doc Viewer"
      src={item?.file?.httpsUrl}
      width="100%"
      height="100%"
      style={{
        overflow: 'scroll',
        display: loading ? 'none' : 'block',
        border: 'none',
      }}
      onLoad={handleLoad}
    ></iframe>
  );

  const containerProps = (mimeType === 'application/zip' && !isFullscreenType)
    ? {
      className: "container-ewa",
      mainElementClassName: `ewa ewa-${viewMode}`,
      closePath,
      backButtonClickFn: onClose,
      closeButtonClickFn: onClose,
    }
    : {
      className: "container-doc-viewer",
      mainElementClassName: `doc-viewer doc-viewer-${viewMode}`,
      barebonesMode: true,
      isGallery: isImage,
      isDocViewer: true,
      backButtonClickFn: onClose,
      closeButtonClickFn: onClose,
      hideCloseButton,
      closePath,
    };

  return (
    <Container {...containerProps} >
      <div className="doc-viewer-header">
        {mimeType !== 'application/zip' && (
          <Header
            type="doc-viewer"
            barebonesMode
            isDocViewer
            headerDesktopTitle={item?.name}
            closeButtonClickFn={onClose}
            hideCloseButton={hideCloseButton}
            closePath={closePath}
          />
        )}
      </div>
      {loading && mimeType !== 'application/zip' && <Spinner type="small" />}
      {mimeType?.includes("video")
        ? renderVideo()
        : isImage
          ? renderImage()
          : mimeType === 'application/zip'
            ? <div id="ewa-viewer-placeholder" />
            : (mimeType === 'application/pdf' && (viewMode === 'mobile' || isSafari))
              ? <PDFViewerLite fileUrl={item?.file?.httpsUrl} loading={loading} setLoading={setLoading} />
              : renderIframe()
      }
    </Container>
  );
};

export default DocumentViewer;